(() => {
  const databaseName = 'nfc-private-designs-v1';
  const lifetime = 7 * 24 * 60 * 60 * 1000;
  let databasePromise;
  function database() {
    if (!window.indexedDB) return Promise.reject(new Error('Private browser storage is unavailable.'));
    if (databasePromise) return databasePromise;
    databasePromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(databaseName, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore('designs', { keyPath: 'id' });
        request.result.createObjectStore('latest');
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
      request.onblocked = () => reject(new Error('Close another editor tab to enable private storage.'));
    });
    return databasePromise;
  }
  async function save(owner, value) {
    const connection = await database();
    const now = Date.now();
    return new Promise((resolve, reject) => {
      const transaction = connection.transaction(['designs', 'latest'], 'readwrite');
      const store = transaction.objectStore('designs');
      const previous = transaction.objectStore('latest').get(owner);
      previous.onsuccess = () => {
        if (!previous.result || previous.result === value.id) return;
        const old = store.get(previous.result);
        old.onsuccess = () => {
          const record = old.result;
          if (record?.owner === owner && !record.successfulDesignId && !record.pending && !record.uncertain) store.delete(record.id);
        };
      };
      store.put({ ...value, owner, updatedAt: now, expiresAt: now + lifetime });
      transaction.objectStore('latest').put(value.id, owner);
      const cursor = store.openCursor();
      cursor.onsuccess = () => {
        const entry = cursor.result;
        if (!entry) return;
        if (entry.value.expiresAt <= now) entry.delete();
        entry.continue();
      };
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error || new Error('Design storage did not complete.'));
    });
  }
  async function load(owner, requestedId) {
    const connection = await database();
    return new Promise((resolve, reject) => {
      const transaction = connection.transaction(['designs', 'latest'], 'readwrite');
      let result = null;
      const getRecord = (identifier) => {
        if (!identifier) return;
        const request = transaction.objectStore('designs').get(identifier);
        request.onsuccess = () => {
          const record = request.result;
          if (record?.owner === owner && record.expiresAt > Date.now()) result = record;
          else if (record?.expiresAt <= Date.now()) transaction.objectStore('designs').delete(record.id);
        };
      };
      if (requestedId) getRecord(requestedId);
      else {
        const request = transaction.objectStore('latest').get(owner);
        request.onsuccess = () => getRecord(request.result);
      }
      transaction.oncomplete = () => resolve(result);
      transaction.onerror = () => reject(transaction.error);
    });
  }
  async function forget(owner) {
    const connection = await database();
    return new Promise((resolve, reject) => {
      const transaction = connection.transaction(['designs', 'latest'], 'readwrite');
      transaction.objectStore('latest').delete(owner);
      const request = transaction.objectStore('designs').openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        if (cursor.value.owner === owner) cursor.delete();
        cursor.continue();
      };
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    });
  }
  window.NFCDesignStore = Object.freeze({ save, load, forget });
})();
